import {React, ui, moment} from 'lib';

import {Req, Res, useWriterApi, useForm, FormItem, Textarea, ApiErrorDialog, ApiSpinnerDialog, ApiCompletionDialog} from 'shared';

import {useStore} from './base';
import {ActionDetailRow} from './action/preview';

const notifiedPath = 'staff/notified';
export type Waiting = ArrayItem<Res<'staff/get_waitings'>>;


export const WaitingCard = ({waiting, showItem = true, headerLabel, onOpenAction, onNotify}: {
    waiting: Waiting;
    showItem?: boolean;
    headerLabel?: string;
    onOpenAction(actionId: number): void;
    onNotify(waiting: Waiting): void;
}): React.ReactElement => {
    const {action} = waiting;
    const openActionProps = {
        textDecoration: 'underline',
        cursor: 'pointer',
        onClick: () => onOpenAction(action.id),
    } as const;
    return <ui.Box border="1px solid #eee" borderRadius="4px" p={4}>
        <ui.Flex justifyContent="space-between">
            {headerLabel
                ? <ui.Text fontWeight="bold" {...openActionProps}>
                    {headerLabel}
                </ui.Text>
                : <ui.Text fontSize="sm" {...openActionProps}>
                    {moment(action.created_at).format('YYYY/MM/DD')}
                </ui.Text>}
            <ui.Button onClick={() => onNotify(waiting)} colorScheme="blue">
                連絡完了
            </ui.Button>
        </ui.Flex>

        <ui.Flex alignItems="center" mt={2}>
            <ui.Text fontWeight="bold">
                {action.customer_name}様
            </ui.Text>

            <ui.Text ml={2} fontSize="small">
                by {action.staff.name}
            </ui.Text>
        </ui.Flex>

        {action.note && <ui.Text mt={2} whiteSpace="pre-line">
            {action.note}
        </ui.Text>}

        {showItem
            ? <WaitingItemPreview waiting={waiting} />
            : <ui.Text mt={2} fontSize="sm">x {waiting.detail.amount}</ui.Text>}
    </ui.Box>;
};


export const WaitingItemPreview = ({waiting}: {waiting: Waiting}) => {
    return <ui.Table>
        <ui.Tbody>
            <ActionDetailRow detail={waiting.detail} canOpenDetail={true} />
        </ui.Tbody>
    </ui.Table>
};


export const NotifyActionDialog = ({waiting, onComplete, ...props}: {
    waiting: Waiting | undefined;
    onComplete(): void;
} & ui.UseDisclosureReturn) => {
    const store = useStore();
    const api = useWriterApi(notifiedPath);
    const {binder, handleSubmit, reset} = useForm<Req<typeof notifiedPath>, 'waiting_id'>(() => ({
        store_id: store.id,
        waiting_id: waiting?.id,
        note: '',
    }));

    React.useEffect(() => {
        if (props.isOpen) {
            reset();
        }
    }, [props.isOpen, waiting, reset]);

    const submit = handleSubmit(data => api.call(data));

    return <ui.Modal {...props}>
        <ui.ModalOverlay />
        <ui.ModalContent>
            <ui.ModalHeader textAlign="center">入荷連絡完了</ui.ModalHeader>
            <ui.ModalCloseButton />

            <ui.ModalBody>
                <FormItem label="Item">
                    {waiting && <WaitingItemPreview waiting={waiting} />}
                </FormItem>

                <FormItem
                    label="note"
                    keyPath="note"
                    error={api.error?.data}
                >
                    <Textarea
                        {...binder.mapInputProps('note')}
                    />
                </FormItem>
            </ui.ModalBody>

            <ui.ModalFooter>
                <ui.Button onClick={props.onClose} mr={3}>キャンセル</ui.Button>
                <ui.Button
                    colorScheme="blue"
                    onClick={submit}
                >
                    確定
                </ui.Button>
            </ui.ModalFooter>
        </ui.ModalContent>

        <ApiErrorDialog api={api} onOk={api.reset} />
        <ApiSpinnerDialog api={api} />
        <ApiCompletionDialog
            api={api}
            onOk={() => {
                onComplete();
                reset();
                api.reset();
            }}
        />
    </ui.Modal>;
};
